'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faFilter,
  faCheck,
  faFileInvoice,
  faUsers,
  faUserDoctor,
  faHospital,
} from '@fortawesome/pro-solid-svg-icons';
import { PulseBadge } from '../../../PulseUI/PulseTable';
import { Tooltip } from 'src/components/PulseUI/Tooltip';

type VolumeOption = 'claims' | 'patients' | 'providers' | 'sites';

const DEFAULT_DISABLED_TOOLTIP = 'This option is disabled';

const options: Array<{ label: string; value: VolumeOption; icon: any }> = [
  { label: 'Claims', value: 'claims' as const, icon: faFileInvoice },
  { label: 'Patients', value: 'patients' as const, icon: faUsers },
  { label: 'Providers', value: 'providers' as const, icon: faUserDoctor },
  { label: 'Sites', value: 'sites' as const, icon: faHospital },
].sort((a, b) => a.label.localeCompare(b.label));

export interface AnalyticsVolumeDropdownProps {
  value: VolumeOption[];
  onChange: (value: VolumeOption[]) => void;
  allowMultiple?: boolean;
  /** Returns true for options that can't be selected in the current context. */
  isOptionDisabled?: (value: VolumeOption) => boolean;
  /** Copy shown in the tooltip when a hovered option is disabled. */
  disabledTooltip?: string;
}

const AnalyticsVolumeDropdown: React.FC<AnalyticsVolumeDropdownProps> = ({
  value,
  onChange,
  allowMultiple = false,
  isOptionDisabled,
  disabledTooltip = DEFAULT_DISABLED_TOOLTIP,
}): React.ReactElement => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleToggleOption = useCallback(
    (optionValue: VolumeOption) => {
      if (isOptionDisabled?.(optionValue)) return;

      if (allowMultiple) {
        // Multi-select mode: toggle in array
        const isSelected = value.includes(optionValue);
        if (isSelected) {
          // Remove from selection
          onChange(value.filter((v) => v !== optionValue));
        } else {
          // Add to selection
          onChange([...value, optionValue]);
        }
      } else {
        // Single-select mode: replace selection and close dropdown
        onChange([optionValue]);
        setIsOpen(false);
      }
    },
    [value, onChange, allowMultiple, isOptionDisabled]
  );

  // Get the selected option label for single-select mode
  const selectedOption =
    !allowMultiple && value.length > 0
      ? options.find((opt) => opt.value === value[0])
      : null;

  return (
    <div
      ref={dropdownRef}
      className="flex flex-col justify-start items-start gap-1 relative min-w-0 "
    >
      <div
        ref={buttonRef}
        className="w-full px-4 bg-white rounded-full border border-neutral-200 flex
        justify-between items-center cursor-pointer hover:bg-neutral-100 h-9 min-w-0"
        onClick={toggleDropdown}
      >
        <div className="flex justify-start items-center gap-2 min-w-0">
          <div className="flex items-center justify-center gap-1.5 min-w-0">
            {selectedOption ? (
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Icon
                  icon={selectedOption.icon}
                  className="text-secondary w-4 h-4"
                />
              </div>
            ) : (
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Icon icon={faFilter} className="text-secondary w-4 h-4" />
              </div>
            )}
            <div className="self-stretch justify-start text-primary text-sm font-medium font-['Inter'] leading-5 line-clamp-1">
              {selectedOption ? selectedOption.label : 'Volume Type'}
            </div>
          </div>
          {allowMultiple && value.length > 0 && (
            <PulseBadge className="bg-sky-50 text-sky-600 border-sky-300 font-medium w-4 h-4 text-xs flex items-center justify-center">
              {value.length}
            </PulseBadge>
          )}
        </div>
        <Icon
          icon={faCaretDown}
          className={`text-neutral-500 w-4 h-4 ml-2 transition-all duration-300
                ${isOpen ? '-rotate-90' : 'rotate-0'}`}
        />
      </div>
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="p-4 fixed bg-white rounded-lg shadow-sm border border-neutral-200 flex flex-col justify-start items-start gap-4 z-50 w-44"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {options.map((option) => {
              const isSelected = value.includes(option.value);
              const isDisabled = !!isOptionDisabled?.(option.value);

              return (
                <Tooltip
                  key={option.value}
                  content={disabledTooltip}
                  position="right"
                  showOnCondition={isDisabled}
                  className="self-stretch w-full"
                >
                  {/* Disabled options stay focusable (aria-disabled rather than
                      the `disabled` attribute) so keyboard users can reach them
                      and the tooltip explaining why still fires on focus. */}
                  <button
                    type="button"
                    className={`self-stretch inline-flex w-full justify-between items-center text-left transition-opacity focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-600 rounded ${
                      isDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:opacity-80'
                    }`}
                    onClick={() => handleToggleOption(option.value)}
                    aria-disabled={isDisabled}
                    aria-pressed={isSelected}
                    data-testid={`volume-option-${option.value}`}
                  >
                    <div className="flex justify-start items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <Icon
                          icon={option.icon}
                          className={`w-4 h-4 ${
                            isSelected ? 'text-sky-600' : 'text-secondary'
                          }`}
                        />
                      </div>
                      <div
                        className={`text-xs font-['Inter'] leading-4 ${
                          isSelected
                            ? 'text-sky-600 font-semibold'
                            : 'text-secondary font-normal'
                        }`}
                      >
                        {option.label}
                      </div>
                    </div>
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      {isSelected && (
                        <Icon icon={faCheck} className="w-3 h-3 text-sky-600" />
                      )}
                    </div>
                  </button>
                </Tooltip>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

export default AnalyticsVolumeDropdown;
